import { useEffect, useRef, useState } from 'react';
import { COUNTRIES_CITIES, PHONE_CODES } from '../data/geoData';
import './FoireCanton.css';

const DEFAULT_SETTINGS = {
  heroTitle: 'Foire de Canton 2026',
  heroLocation: 'Guangzhou, Chine',
  heroSubtitle: "La prochaine édition de la Foire de Canton à Guangzhou aura lieu à l'automne 2026 ! Inscrivez-vous dès maintenant et bénéficiez de l'accompagnement complet de GSSG.",
  phases: [
    { id: 'phase1', dates: '15 au 19 octobre 2026' },
    { id: 'phase2', dates: '23 au 27 octobre 2026' },
    { id: 'phase3', dates: '31 octobre au 4 novembre 2026' },
  ],
};

const PHASES = [
  {
    id: 'phase1',
    label: 'Phase 1 — 15 au 19 octobre 2026',
    dates: '15 au 19 octobre 2026',
    categories: [
      'Électronique & électroménager',
      'Éclairage',
      'Véhicules & pièces détachées',
      'Machines & équipements',
      'Quincaillerie & outils',
      'Matériaux de construction',
      'Énergie',
    ],
  },
  {
    id: 'phase2',
    label: 'Phase 2 — 23 au 27 octobre 2026',
    dates: '23 au 27 octobre 2026',
    categories: [
      'Jouets & jeux',
      'Cadeaux & objets publicitaires',
      'Décoration intérieure',
      'Articles ménagers',
      'Maroquinerie & bagages',
      'Montres & horlogerie',
      'Cosmétiques & hygiène',
    ],
  },
  {
    id: 'phase3',
    label: 'Phase 3 — 31 octobre au 4 novembre 2026',
    dates: '31 octobre au 4 novembre 2026',
    categories: [
      'Textiles & habillement',
      'Chaussures',
      'Alimentation & produits agricoles',
      'Dispositifs médicaux & santé',
      'Articles de sport & loisirs',
      'Fournitures de bureau',
    ],
  },
];

const SERVICES_OPTIONS = [
  'Visa',
  'Billets avion',
  'Hébergement',
  'Accueil aéroport',
  'Accompagnement et traduction',
];

const COUNTRIES = COUNTRIES_CITIES.map(c => c.pays);

const INIT = {
  nom: '',
  prenom: '',
  email: '',
  indicatifPays: 'France',
  telephone: '',
  typeParticipant: '',
  nombreParticipants: '1',
  phases: [],
  services: [],
  paysResidence: '',
  message: '',
};

export default function FoireCanton() {
  const formRef = useRef(null);
  const [form, setForm] = useState(INIT);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  useEffect(() => {
    fetch('http://localhost:5001/api/content/canton-fair')
      .then(r => r.json())
      .then(d => setSettings(d))
      .catch(() => {});
  }, []);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    if (errors[name]) setErrors(er => ({ ...er, [name]: '' }));
  };

  const handlePhone = (e) => {
    setForm(f => ({ ...f, telephone: e.target.value.replace(/[^\d\s\-]/g, '') }));
  };

  const handlePhaseToggle = (id) => {
    setForm(f => {
      const phases = f.phases.includes(id)
        ? f.phases.filter(p => p !== id)
        : [...f.phases, id];
      return { ...f, phases };
    });
    if (errors.phases) setErrors(er => ({ ...er, phases: '' }));
  };

  const handleServiceToggle = (svc) => {
    setForm(f => {
      const services = f.services.includes(svc)
        ? f.services.filter(s => s !== svc)
        : [...f.services, svc];
      return { ...f, services };
    });
  };

  const validate = () => {
    const e = {};
    if (!form.nom.trim()) e.nom = 'Le nom est requis.';
    if (!form.email.trim()) e.email = "L'email est requis.";
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Email invalide.';
    if (!form.typeParticipant) e.typeParticipant = 'Veuillez choisir un type.';
    if (form.phases.length === 0) e.phases = 'Veuillez sélectionner au moins une phase.';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setSending(true);
    try {
      const indicatif = PHONE_CODES.find(p => p.pays === form.indicatifPays);
      const payload = {
        ...form,
        indicatifPays: indicatif ? `${indicatif.flag} ${indicatif.code}` : '',
        phases: form.phases.map(id => PHASES.find(p => p.id === id)?.label || id),
      };
      const res = await fetch('http://localhost:5001/api/foire-canton', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();
      setSent(true);
    } catch {
      setErrors({ submit: "Une erreur s'est produite. Veuillez réessayer." });
    } finally {
      setSending(false);
    }
  };

  const handleReset = () => {
    setForm(INIT);
    setErrors({});
    setSent(false);
  };

  return (
    <div className="fc-page">
      {/* ── Hero ── */}
      <section className="fc-hero">

        {/* Left: Animated Chinese flag */}
        <div className="fc-hero__deco fc-hero__deco--left" aria-hidden="true">
          <div className="fc-flag-wrap">
            <div className="fc-flag-pole" />
            <svg className="fc-china-flag" viewBox="0 0 450 300" xmlns="http://www.w3.org/2000/svg">
              <rect width="450" height="300" fill="#DE2910" />
              {/* Large star */}
              <polygon fill="#FFDE00"
                points="75,30 85.1,61.1 117.8,61.1 91.4,80.3 101.5,111.4 75,92.2 48.5,111.4 58.6,80.3 32.2,61.1 64.9,61.1" />
              {/* Small stars – rotated to point toward large star center */}
              <polygon fill="#FFDE00" transform="translate(150,30) rotate(239)" points="0,-15 3.4,-4.6 14.3,-4.6 5.4,1.8 8.8,12.1 0,5.7 -8.8,12.1 -5.4,1.8 -14.3,-4.6 -3.4,-4.6" />
              <polygon fill="#FFDE00" transform="translate(180,60) rotate(262)" points="0,-15 3.4,-4.6 14.3,-4.6 5.4,1.8 8.8,12.1 0,5.7 -8.8,12.1 -5.4,1.8 -14.3,-4.6 -3.4,-4.6" />
              <polygon fill="#FFDE00" transform="translate(180,105) rotate(286)" points="0,-15 3.4,-4.6 14.3,-4.6 5.4,1.8 8.8,12.1 0,5.7 -8.8,12.1 -5.4,1.8 -14.3,-4.6 -3.4,-4.6" />
              <polygon fill="#FFDE00" transform="translate(150,135) rotate(309)" points="0,-15 3.4,-4.6 14.3,-4.6 5.4,1.8 8.8,12.1 0,5.7 -8.8,12.1 -5.4,1.8 -14.3,-4.6 -3.4,-4.6" />
            </svg>
          </div>
          <div className="fc-hero__sparkles">
            <span className="fc-sparkle fc-sparkle--1">✦</span>
            <span className="fc-sparkle fc-sparkle--2">✦</span>
            <span className="fc-sparkle fc-sparkle--3">✦</span>
            <span className="fc-sparkle fc-sparkle--4">✦</span>
          </div>
        </div>

        {/* Center content */}
        <div className="fc-hero__center">
          <span className="fc-hero__badge">Le Saviez-Vous ?</span>
          <h1 className="fc-hero__title">
            {settings.heroTitle}<br />
            <span>{settings.heroLocation}</span>
          </h1>
          <p className="fc-hero__subtitle">
            {settings.heroSubtitle}
          </p>
          <button className="fc-hero__cta" onClick={scrollToForm}>
            S'inscrire maintenant
          </button>
        </div>

        {/* Right: Animated lanterns */}
        <div className="fc-hero__deco fc-hero__deco--right" aria-hidden="true">
          <span className="fc-anim-lantern fc-anim-lantern--1">🏮</span>
          <span className="fc-anim-lantern fc-anim-lantern--2">🏮</span>
          <span className="fc-anim-lantern fc-anim-lantern--3">🏮</span>
        </div>

      </section>

      {/* ── Phases ── */}
      <section className="fc-phases">
        <div className="fc-phases__inner">
          <span className="fc-section-label">Calendrier</span>
          <h2 className="fc-section-title">Les 3 phases de la Foire de Canton</h2>
          <div className="fc-phases__grid">
            {PHASES.map((phase, i) => {
              const dynDates = settings.phases?.[i]?.dates || phase.dates;
              return (
                <div key={phase.id} className="fc-phase-card">
                  <div className="fc-phase-card__num">{i + 1}</div>
                  <div className="fc-phase-card__title">Phase {i + 1}</div>
                  <div className="fc-phase-card__dates">{dynDates}</div>
                  <ul className="fc-phase-card__cats">
                    {phase.categories.map(cat => (
                      <li key={cat}>{cat}</li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Accompagnement ── */}
      <section className="fc-accomp">
        <div className="fc-accomp__inner">
          <div className="fc-accomp__text">
            <h3>GSSG vous accompagne à chaque étape</h3>
            <p>
              Global Sourcing Solution Group organise votre participation à la Foire de Canton
              de A à Z. Grâce à notre présence locale en Chine et notre expertise du marché,
              nous prenons en charge toute la logistique pour que vous puissiez vous concentrer
              sur l'essentiel : trouver les meilleurs fournisseurs et développer vos affaires.
            </p>
            <ul className="fc-services-list">
              <li>Assistance visa et démarches administratives</li>
              <li>Réservation de billets d'avion au meilleur tarif</li>
              <li>Hébergement à proximité du site de la foire</li>
              <li>Accueil et transfert depuis l'aéroport</li>
              <li>Accompagnement sur place et traduction professionnelle</li>
            </ul>
          </div>
          <div className="fc-accomp__visual">
            <div className="fc-stat">
              <div className="fc-stat__icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                </svg>
              </div>
              <div>
                <div className="fc-stat__label">Durée de chaque phase</div>
                <div className="fc-stat__value">5 jours</div>
              </div>
            </div>
            <div className="fc-stat">
              <div className="fc-stat__icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              </div>
              <div>
                <div className="fc-stat__label">Exposants par édition</div>
                <div className="fc-stat__value">25 000+</div>
              </div>
            </div>
            <div className="fc-stat">
              <div className="fc-stat__icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="2" y1="12" x2="22" y2="12"/>
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                </svg>
              </div>
              <div>
                <div className="fc-stat__label">Acheteurs du monde entier</div>
                <div className="fc-stat__value">190+ pays représentés</div>
              </div>
            </div>
            <div className="fc-stat">
              <div className="fc-stat__icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                </svg>
              </div>
              <div>
                <div className="fc-stat__label">Présence GSSG en Chine</div>
                <div className="fc-stat__value">10 ans d'expérience</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Form ── */}
      <section className="fc-form-section" ref={formRef}>
        <div className="fc-form-section__inner">
          <span className="fc-section-label">Inscription</span>
          <h2 className="fc-section-title">Formulaire d'inscription</h2>

          <div className="fc-form">
            {sent ? (
              <div className="fc-form__success">
                <h3>Inscription envoyée !</h3>
                <p>Nous avons bien reçu votre demande et reviendrons vers vous dans les plus brefs délais.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                <div className="fc-form__grid">
                  {/* Nom */}
                  <div className="fc-form__group">
                    <label className="fc-form__label fc-form__label--required">Nom</label>
                    <input
                      className={`fc-form__input${errors.nom ? ' fc-form__input--error' : ''}`}
                      name="nom"
                      value={form.nom}
                      onChange={handleChange}
                      placeholder="Votre nom"
                    />
                    {errors.nom && <span className="fc-form__error">{errors.nom}</span>}
                  </div>

                  {/* Prénom */}
                  <div className="fc-form__group">
                    <label className="fc-form__label">Prénom</label>
                    <input
                      className="fc-form__input"
                      name="prenom"
                      value={form.prenom}
                      onChange={handleChange}
                      placeholder="Votre prénom"
                    />
                  </div>

                  {/* Email */}
                  <div className="fc-form__group">
                    <label className="fc-form__label fc-form__label--required">Email</label>
                    <input
                      className={`fc-form__input${errors.email ? ' fc-form__input--error' : ''}`}
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="votre@email.com"
                    />
                    {errors.email && <span className="fc-form__error">{errors.email}</span>}
                  </div>

                  {/* Téléphone */}
                  <div className="fc-form__group">
                    <label className="fc-form__label">Téléphone</label>
                    <div className="fc-form__phone-wrap">
                      <select
                        className="fc-form__indicatif"
                        name="indicatifPays"
                        value={form.indicatifPays}
                        onChange={handleChange}
                      >
                        {PHONE_CODES.map(p => (
                          <option key={p.pays} value={p.pays}>
                            {p.flag} {p.code}
                          </option>
                        ))}
                      </select>
                      <input
                        className="fc-form__tel-input"
                        type="tel"
                        name="telephone"
                        value={form.telephone}
                        onChange={handlePhone}
                        placeholder="Numéro"
                        inputMode="numeric"
                      />
                    </div>
                  </div>

                  {/* Type de participant */}
                  <div className="fc-form__group">
                    <label className="fc-form__label fc-form__label--required">Type de participant</label>
                    <select
                      className={`fc-form__select${errors.typeParticipant ? ' fc-form__select--error' : ''}`}
                      name="typeParticipant"
                      value={form.typeParticipant}
                      onChange={handleChange}
                    >
                      <option value="">-- Sélectionner --</option>
                      <option value="Entreprise">Entreprise</option>
                      <option value="Particulier">Particulier</option>
                    </select>
                    {errors.typeParticipant && <span className="fc-form__error">{errors.typeParticipant}</span>}
                  </div>

                  {/* Nombre de participants */}
                  <div className="fc-form__group">
                    <label className="fc-form__label">Nombre de participants</label>
                    <input
                      className="fc-form__input"
                      type="number"
                      name="nombreParticipants"
                      value={form.nombreParticipants}
                      onChange={handleChange}
                      min="1"
                      max="50"
                    />
                  </div>

                  {/* Phases */}
                  <div className="fc-form__group fc-form__group--full">
                    <label className="fc-form__label fc-form__label--required">Phases souhaitées</label>
                    <div className="fc-form__phase-checks">
                      {PHASES.map((phase, i) => {
                        const dynDates = settings.phases?.[i]?.dates || phase.dates;
                        return (
                          <label key={phase.id} className={`fc-form__phase-item${form.phases.includes(phase.id) ? ' fc-form__phase-item--selected' : ''}`}>
                            <div className="fc-form__phase-header">
                              <input
                                type="checkbox"
                                checked={form.phases.includes(phase.id)}
                                onChange={() => handlePhaseToggle(phase.id)}
                              />
                              <span className="fc-form__phase-title">Phase {i + 1} — {dynDates}</span>
                            </div>
                            <ul className="fc-form__phase-cats">
                              {phase.categories.map(cat => <li key={cat}>{cat}</li>)}
                            </ul>
                          </label>
                        );
                      })}
                    </div>
                    {errors.phases && <span className="fc-form__error">{errors.phases}</span>}
                  </div>

                  {/* Services */}
                  <div className="fc-form__group fc-form__group--full">
                    <label className="fc-form__label">Services souhaités</label>
                    <div className="fc-form__checks">
                      {SERVICES_OPTIONS.map(svc => (
                        <label key={svc} className="fc-form__check-label">
                          <input
                            type="checkbox"
                            checked={form.services.includes(svc)}
                            onChange={() => handleServiceToggle(svc)}
                          />
                          {svc}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Pays de résidence */}
                  <div className="fc-form__group fc-form__group--full">
                    <label className="fc-form__label">Pays de résidence</label>
                    <select
                      className="fc-form__select"
                      name="paysResidence"
                      value={form.paysResidence}
                      onChange={handleChange}
                    >
                      <option value="">-- Sélectionner votre pays --</option>
                      {COUNTRIES.map(pays => (
                        <option key={pays} value={pays}>{pays}</option>
                      ))}
                    </select>
                  </div>

                  {/* Message */}
                  <div className="fc-form__group fc-form__group--full">
                    <label className="fc-form__label">Message / Informations complémentaires</label>
                    <textarea
                      className="fc-form__textarea"
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Précisez vos besoins ou questions..."
                    />
                  </div>
                </div>

                {errors.submit && (
                  <p className="fc-form__error" style={{ marginTop: 12 }}>{errors.submit}</p>
                )}

                <div className="fc-form__actions">
                  <button type="submit" className="fc-form__btn-submit" disabled={sending}>
                    {sending ? 'Envoi en cours...' : "Envoyer mon inscription"}
                  </button>
                  <button type="button" className="fc-form__btn-cancel" onClick={handleReset}>
                    Annuler
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
